import { SideNavItem } from "@design-system/SideNav/SideNavItem";
import { SideNavSection } from "@design-system/SideNav/SideNavSection";
import { SideNavSubItem } from "@design-system/SideNav/SideNavSubItem";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { BillPayIcon, InvoicingIcon } from "@icons";
import { useNavigationShortcutKeys } from "./useNavigationShortcutKeys";
import { useRouteExpansion } from "./useRouteExpansion";
import type { SideNavSectionState } from "./sideNavSection.types";

const INVOICING_PATH = "/invoicing";
const INVOICING_INBOX_PATH = `${INVOICING_PATH}/inbox`;
const INVOICING_ITEMS_PATH = `${INVOICING_PATH}/items`;
const INVOICING_TEMPLATES_PATH = `${INVOICING_PATH}/templates`;
const BILL_PAY_PATH = "/billing";
const BILL_PAY_INBOX_PATH = `${BILL_PAY_PATH}/inbox`;

/** Invoicing, expanding into its drafts Inbox, Items and Templates. */
const InvoicingRow = () => {
    const { draftsTab: hasDraftsTab } = useTypedFlags();
    const shortcutKeys = useNavigationShortcutKeys();
    const expansion = useRouteExpansion({
        parentPath: INVOICING_PATH,
        subItemPaths: [
            ...(hasDraftsTab ? [INVOICING_INBOX_PATH] : []),
            INVOICING_ITEMS_PATH,
            INVOICING_TEMPLATES_PATH,
        ],
    });

    return (
        <SideNavItem
            to={INVOICING_PATH}
            end
            icon={<InvoicingIcon />}
            label="Invoicing"
            shortcut={shortcutKeys(INVOICING_PATH)}
            {...expansion}
        >
            {hasDraftsTab && (
                <SideNavSubItem to={INVOICING_INBOX_PATH} end label="Inbox" />
            )}
            <SideNavSubItem to={INVOICING_ITEMS_PATH} end label="Items" />
            <SideNavSubItem
                to={INVOICING_TEMPLATES_PATH}
                end
                label="Templates"
            />
        </SideNavItem>
    );
};

const BILL_PAY_ROW_PROPS = {
    to: BILL_PAY_PATH,
    end: true,
    icon: <BillPayIcon />,
    label: "Bill pay",
};

/** Bill pay, expanding into its drafts Inbox. */
const BillPayWithInboxRow = ({ shortcut }: { shortcut?: string }) => {
    const expansion = useRouteExpansion({
        parentPath: BILL_PAY_PATH,
        subItemPaths: [BILL_PAY_INBOX_PATH],
    });

    return (
        <SideNavItem {...BILL_PAY_ROW_PROPS} shortcut={shortcut} {...expansion}>
            <SideNavSubItem to={BILL_PAY_INBOX_PATH} end label="Inbox" />
        </SideNavItem>
    );
};

const BillPayRow = () => {
    const { draftsTab: hasDraftsTab } = useTypedFlags();
    const shortcut = useNavigationShortcutKeys()(BILL_PAY_PATH);

    return hasDraftsTab ? (
        <BillPayWithInboxRow shortcut={shortcut} />
    ) : (
        <SideNavItem {...BILL_PAY_ROW_PROPS} shortcut={shortcut} />
    );
};

/** Invoicing with its Inbox, Items and Templates, and Bill pay with its Inbox. */
export const PaymentsSection = ({
    section,
}: {
    section: SideNavSectionState;
}) => (
    <SideNavSection {...section}>
        <InvoicingRow />
        <BillPayRow />
    </SideNavSection>
);
